import React, { createContext, useState, useEffect, useContext } from "react";
import Utils from "../configs/Utils";
import Enums from "../configs/Enums";

// Create AuthContext to hold authentication state and actions
export const AuthContext = createContext(undefined);

// Provider component to supply authentication-related data and actions to child components
export const AuthContextProvider = ({ children }) => {
  // Define state variables
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!currentUser?._id;

  // Fetch authenticated user data on mount (cookie is sent automatically via credentials: 'include')
  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const data = await Utils._apiRequest({
          path: Enums.endpoints.auth.ME,
          type: "GET",
        });
        if (data.errors) {
          setCurrentUser(null);
        } else {
          // Set user data upon successful authentication
          setCurrentUser(data);
        }
      } catch (err) {
        console.log("request Auth error: ", err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAuth();
  }, []);

  // Logout function: call server to clear httpOnly cookies
  const logout = async () => {
    try {
      await Utils._apiRequest({
        path: Enums.endpoints.auth.LOGOUT,
        type: "POST",
      });
    } catch (err) {
      console.log("Logout error: ", err);
    } finally {
      setCurrentUser(null);
    }
  };

  // Provide `currentUser`, `setCurrentUser`, `loading`, `isAuthenticated`, `logout` to child components
  return (
    <AuthContext.Provider
      value={{ currentUser, setCurrentUser, loading, isAuthenticated, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};
